import React, { useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import SearchListPopover from '../../ui/SearchListPopover';
import type { SearchOption } from '../../ui/filterOptionsByWords';
import './placeHeader.css';

export interface TitleSwitchMenu {
  /** "account" or "location": names the button ("…, switch account"). */
  noun: 'account' | 'location';
  options: SearchOption[];
  /** The option on screen (ticked). */
  value: string;
  onSelect: (value: string) => void;
  /** Busy while the session moves to another account. */
  busy?: boolean;
  testIdPrefix: string;
}

/**
 * The page's name as its own switcher: "Splits Demo Motors ▾" opens a
 * searchable list of the other accounts (or locations). Without a menu it is
 * just the heading.
 */
export const TitleSwitch: React.FC<{ title: string; menu?: TitleSwitchMenu | null; level?: 1 | 2 }> = ({ title, menu, level = 1 }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  const Heading = level === 1 ? 'h1' : 'h2';
  if (!menu) return <Heading className="ph-title">{title}</Heading>;
  return (
    <>
      <Heading className="ph-title">
        <button
          ref={ref}
          type="button"
          className="ph-title-switch"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-busy={menu.busy || undefined}
          // aria-disabled, not disabled: keyboard focus stays while the session moves.
          aria-disabled={menu.busy || undefined}
          aria-label={`${title}, switch ${menu.noun}`}
          title={`Switch ${menu.noun}`}
          data-testid={menu.testIdPrefix}
          onClick={() => {
            if (!menu.busy) setOpen((o) => !o);
          }}
        >
          <span>{menu.busy ? 'Switching…' : title}</span>
          <ChevronDown size={20} aria-hidden="true" className="ph-title-switch-chevron" />
        </button>
      </Heading>
      <SearchListPopover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={ref}
        options={menu.options}
        value={menu.value}
        onSelect={(v) => {
          setOpen(false);
          menu.onSelect(v);
        }}
        listLabel={menu.noun === 'account' ? 'Accounts' : 'Locations'}
        searchPlaceholder={menu.noun === 'account' ? 'Find an account' : 'Find a location'}
        testIdPrefix={menu.testIdPrefix}
        align="left"
      />
    </>
  );
};

export default TitleSwitch;
